import { ComposableMap, Geographies, Geography, Marker, Annotation } from "react-simple-maps";

// World-atlas countries TopoJSON (ISO numeric ids)
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Africa ISO numeric ids (incl. island nations)
const AFRICA_IDS = new Set([
  "012","024","072","086","108","120","132","140","148","174","178","180","204","226","231","232",
  "262","266","270","288","324","384","404","426","430","434","450","454","466","478","480","504",
  "508","516","562","566","624","646","654","678","686","690","694","706","710","716","728","729",
  "732","748","768","788","800","818","834","854","894"
]);
const UGANDA_ID = "800";

const ORIGIN = { lon: 32.58, lat: 1.37, label: "UGANDA · KAMPALA" }; // Kampala
const DESTS = [
  { lon: -0.13, lat: 51.51, label: "LONDON" },
  { lon: 8.68, lat: 50.11, label: "FRANKFURT" },
  { lon: 4.35, lat: 50.85, label: "EU · BRUSSELS" },
];

/**
 * AfricaMap — real geographic map of Africa with Uganda highlighted and
 * animated dotted export routes to London / Frankfurt / EU.
 */
export const AfricaMap = () => {
  return (
    <div data-testid="africa-map-hero" className="relative">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 480, center: [18, 18] }}
        width={600}
        height={680}
        style={{ width: "100%", height: "auto" }}
      >
        <Geographies geography={geoUrl}>
          {({ geographies, projection }) => {
            const renderRoutes = () => {
              const o = projection([ORIGIN.lon, ORIGIN.lat]);
              if (!o) return null;
              return DESTS.map((d, i) => {
                const p = projection([d.lon, d.lat]);
                if (!p) return null;
                const mx = (o[0] + p[0]) / 2;
                const my = Math.min(o[1], p[1]) - 70 - i * 12;
                return (
                  <path
                    key={d.label}
                    d={`M${o[0]},${o[1]} Q${mx},${my} ${p[0]},${p[1]}`}
                    fill="none"
                    stroke="#C7744E"
                    strokeWidth="1.2"
                    className="route-line"
                  />
                );
              });
            };
            return (
              <>
                {geographies.map((geo) => {
                  const isAfrica = AFRICA_IDS.has(geo.id);
                  const isUganda = geo.id === UGANDA_ID;
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      style={{
                        default: {
                          fill: isUganda ? "#0C3B33" : isAfrica ? "rgba(12,59,51,0.10)" : "rgba(12,59,51,0.04)",
                          stroke: isAfrica ? "rgba(12,59,51,0.35)" : "rgba(12,59,51,0.12)",
                          strokeWidth: 0.5,
                          outline: "none",
                        },
                        hover: { fill: isUganda ? "#082B25" : isAfrica ? "rgba(12,59,51,0.18)" : "rgba(12,59,51,0.05)", outline: "none" },
                        pressed: { outline: "none" },
                      }}
                    />
                  );
                })}
                {renderRoutes()}
              </>
            );
          }}
        </Geographies>

        {/* Origin marker — Uganda */}
        <Marker coordinates={[ORIGIN.lon, ORIGIN.lat]}>
          <circle r={9} fill="none" stroke="#C7744E" strokeOpacity="0.5" strokeWidth="1">
            <animate attributeName="r" values="9;20;9" dur="3.4s" repeatCount="indefinite" />
            <animate attributeName="stroke-opacity" values="0.55;0;0.55" dur="3.4s" repeatCount="indefinite" />
          </circle>
          <circle r={4.5} fill="#C7744E" />
        </Marker>
        <Annotation
          subject={[ORIGIN.lon, ORIGIN.lat]}
          dx={28}
          dy={8}
          connectorProps={{ stroke: "#C7744E", strokeWidth: 0.6, strokeLinecap: "round" }}
        >
          <text x={4} y={3} fontFamily="Manrope" fontSize={9} fill="#1D1D1B" letterSpacing={1.4}>
            {ORIGIN.label}
          </text>
        </Annotation>

        {/* Destination markers */}
        {DESTS.map((d, i) => (
          <Marker key={d.label} coordinates={[d.lon, d.lat]}>
            <circle r={7} fill="none" stroke="#0C3B33" strokeOpacity="0.45" strokeWidth="0.9">
              <animate attributeName="r" values="7;15;7" dur="3.6s" begin={`${i * 0.5}s`} repeatCount="indefinite" />
              <animate attributeName="stroke-opacity" values="0.5;0;0.5" dur="3.6s" begin={`${i * 0.5}s`} repeatCount="indefinite" />
            </circle>
            <circle r={3} fill="#0C3B33" />
            <text x={8} y={3} fontFamily="Manrope" fontSize={8.5} fill="#5C615E" letterSpacing={1.4}>
              {d.label}
            </text>
          </Marker>
        ))}
      </ComposableMap>
    </div>
  );
};

export default AfricaMap;
